import React, { useState, useEffect, useMemo, useContext, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { css } from '@emotion/core'
import { AdditionalGuestInput } from '@/models/self-checkin/accommodation-info'
import { getIdentificationRequirement, hasRequiredJapaneseIdentityDocumentImages, IdentificationRequirement } from '@/libs/self-checkin'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { IdentityVerifyCamera } from '@/components/organisms/self-checkin/identity-verify/identity-verify-camera'
import { SelfCheckinContentCard } from '@/components/molecules/self-checkin-content-card'
import { SelectableTimeSelectionType, SelectedTime, SpaceAssign } from '@/models/self-checkin/custom-checkin'
import { LoaderContextCreator } from '@/contexts/loader'
import { CancelSpaceReservationFromSelfCI, fetchTimeSelectionSpace } from '@/apis/aipass'
import { TimeSelectionForm } from '@/components/organisms/self-checkin/time-selection-form'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { PluginCode, checkActivePlugin } from '@/libs/plugins'
import { FrontTabletFormLabel } from '@/components/molecules/front-tablet/input/form-label'
import { FrontTabletInputField } from '@/components/molecules/front-tablet/input/input-field'
import { ErrorMessage } from '@/components/molecules/front-tablet/input/error-message'
import { AdditionalGuestForm } from '@/components/organisms/self-checkin/additional-guest-form'
import { CustomCheckin } from '@/components/organisms/self-checkin/custom-checkin'
import { addValidateGuidesRequired, changeValidateGuidesRequired } from '@/utils/guide'
import { createSelfCheckinResponsiveStyle, FrontTabletResponsiveToken } from '@/utils/front-tablet-responsive-style'

export const AdditionalGuestInfoEdit: React.FC<{}> = () => {
  const { t, i18n } = useTranslation()
  const location = useLocation<{ anchor: string }>()
  const { state, saveState, stateErrorHandling } = useSelfCheckInState()
  const { isLoading, setIsLoading } = useContext(LoaderContextCreator())

  const [guides, setGuides] = useState<any>([])
  const [selectedInputValue, setSelectedInputValue] = useState<any>([])
  const [additionalGuestInfo, setAdditionalGuestInfo] = useState<AdditionalGuestInput>(state.accommodationInfo as AdditionalGuestInput)
  const [inputReceiptName, setInputReceiptName] = useState<string>(state.confirm?.payment?.receiptName || '')
  const [isFormValidRequiredFields, setIsFormValidRequiredFields] = useState(true)
  const [validGuides, setValidGuides] = useState<any>([])
  const [isLastNameValid, setIsLastNameValid] = useState(true)
  const [isFirstNameValid, setIsFirstNameValid] = useState(true)
  const [activateCameraType, setActivateCameraType] = useState<'front' | 'back' | 'representativePassport' | undefined>(undefined)
  const [identificationFrontImage, setIdentificationFrontImage] = useState<string | undefined>(
    state.uploadLicense?.identificationFrontImage,
  )
  const [identificationFrontObjectKey, setIdentificationFrontObjectKey] = useState<string | undefined>(
    state.uploadLicense?.identificationFrontObjectKey,
  )
  const [identificationBackImage, setIdentificationBackImage] = useState<string | undefined>(state.uploadLicense?.identificationBackImage)
  const [identificationBackObjectKey, setIdentificationBackObjectKey] = useState<string | undefined>(
    state.uploadLicense?.identificationBackObjectKey,
  )
  const [representativePassportImage, setRepresentativePassportImage] = useState<string | undefined>(
    state.uploadLicense?.representativePassportImage,
  )
  const [representativePassportObjectKey, setRepresentativePassportObjectKey] = useState<string | undefined>(
    state.uploadLicense?.representativePassportObjectKey,
  )
  const [nationality, setNationality] = useState<string | undefined>(state.uploadLicense?.nationality)
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)
  const lang = i18n.language
  const history = useHistory()
  const [isRequiredTimeSelection, setIsRequiredTimeSelection] = useState(false)
  const [selectedTimes, setSelectedTimes] = useState<SelectedTime>({})
  const [assignSpaces, setAssignSpaces] = useState<SpaceAssign[]>([])
  const [timeSelections, setTimeSelections] = useState<SelectableTimeSelectionType[]>([])

  const listValidateProps = useRef(['firstName', 'lastName'])

  const checkValidateFormRequired = () => {
    for (const property in additionalGuestInfo) {
      if (listValidateProps.current.includes(property)) {
        if (
          !additionalGuestInfo[property] ||
          (typeof additionalGuestInfo[property] === 'string' && !additionalGuestInfo[property].trim())
        ) {
          return false
        }
      }
    }
    const validAccompany = additionalGuestInfo?.accompany?.filter(v => !v.lastName.trim() || !v.firstName.trim()) ?? []
    if (!additionalGuestInfo.hasContactForAllGuest && validAccompany?.length > 0) {
      return false
    }
    if (!isValidInputReceiptName) {
      return false
    }

    return true
  }

  const checkOnlyAnphabet = value => {
    return /^[a-zA-Z\s]*$/.test(value)
  }

  const isDisabled = (): boolean => {
    const isValid = checkValidateFormRequired()
    if (!isValid) {
      return true
    }
    return isRequiredTimeSelection || isLoading
  }

  const onClickConfirm = () => {
    replaceInputValue()
    const nav = t('Information from the facility')
    const notes = guides.map(guide => {
      if (guide.inputType !== 'none') {
        return `${guide.jpTitle}:${guide.selectedAnswer}`
      }
    })
    const filteredNotes = notes.filter(v => v)
    let checkinMemo: string = ''
    if (filteredNotes.length !== 0) {
      checkinMemo = [nav, ...filteredNotes].join('\n')
    } else {
      checkinMemo = ''
    }
    submit(checkinMemo)
  }

  // Replace form content with input content, not filled in with -
  const replaceInputValue = () => {
    const _guides = [...guides]
    _guides.map(guide => {
      selectedInputValue.map(v => {
        if (v.customCheckinId === guide.customCheckinId) {
          // When you reselect Please select
          if (v.value === '') {
            guide.selectedAnswer = '-'
          } else {
            guide.selectedAnswer = v.value
          }
        }
      })
      // when not choosing
      if (!guide.selectedAnswer) {
        guide.selectedAnswer = '-'
      }
    })
    setGuides(_guides)
  }

  const submit = async checkinMemo => {
    setIsFirstNameValid(true)
    setIsLastNameValid(true)
    const isValid = checkValidateFormRequired()
    if (!isValid) {
      return
    }
    const checkLastName = !(additionalGuestInfo.nationality == 'japanese' && lang === 'ja')
      ? checkOnlyAnphabet(additionalGuestInfo.lastName)
      : true
    const checkFirstName = !(additionalGuestInfo.nationality === 'japanese' && lang === 'ja')
      ? checkOnlyAnphabet(additionalGuestInfo.firstName)
      : true

    setIsFirstNameValid(checkFirstName)
    setIsLastNameValid(checkLastName)

    const isValidForm = checkFirstName && checkLastName
    if (!isValidForm) {
      // set the name of the error message
      let scrollName = ''
      switch (true) {
        case !checkLastName:
          scrollName = 'lastName'
          break
        case !checkFirstName:
          scrollName = 'firstName'
          break
        default:
          // scroll to top
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
          break
      }
      if (scrollName) {
        // get element by name
        const scrollErrorName = document.getElementsByName(scrollName)
        // Scroll to target element
        scrollErrorName[0]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
      return
    }

    saveState({
      ...state,
      // 支払いありのとき、宛名欄が確定値から変更されていれば手入力として確定宛名を更新する
      confirm:
        state.confirm?.payment && inputReceiptName !== state.confirm.payment.receiptName
          ? {
              ...state.confirm,
              payment: {
                ...state.confirm.payment,
                receiptName: inputReceiptName,
                isReceiptNameManual: true,
              },
            }
          : state.confirm,
      checkinCustomize: state.checkinCustomize
        ? {
            ...state.checkinCustomize,
            assignSpaces,
            selectedTime: selectedTimes,
            timeSelections,
          }
        : undefined,
      uploadLicense: {
        nationality,
        documentType: state.uploadLicense?.documentType,
        identificationFrontImage,
        identificationFrontObjectKey,
        identificationBackImage,
        identificationBackObjectKey,
        representativePassportImage,
        representativePassportObjectKey,
        companionImageArray: state.uploadLicense?.companionImageArray,
      },
      accommodationInfo: additionalGuestInfo,
    })

    const identificationRequirement = getIdentificationRequirement(state, additionalGuestInfo.nationality)
    if (identificationRequirement === IdentificationRequirement.License) {
      if (!hasRequiredJapaneseIdentityDocumentImages(state.uploadLicense)) {
        history.replace({ pathname: '/self-checkin/identity-verify/upload-license' })
        return
      }
    }
    if (identificationRequirement === IdentificationRequirement.Passport) {
      if (!state.uploadLicense?.representativePassportImage) {
        history.replace({ pathname: '/self-checkin/identity-verify/upload-passport' })
        return
      }
    }
    history.goBack()
  }

  const filterUniqueValueById = (values: Array<{ customCheckinId: string; value: string }>) => {
    const ids = values.map(v => v.customCheckinId)
    return values.filter((v, index) => ids.lastIndexOf(v.customCheckinId) === index)
  }

  const onChangeState = (e: React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLTextAreaElement>, customCheckinId) => {
    if (!e.target.value) {
      const addValidateGuides = addValidateGuidesRequired(customCheckinId, guides)
      if (addValidateGuides.length > 0) {
        const errorMessage = document.getElementById(addValidateGuides[0]['customCheckinId'] + '-error')
        if (errorMessage) {
          errorMessage.style.visibility = 'visible'
        }
      }
      setValidGuides(addValidateGuides.concat(validGuides))
    } else {
      const changeValidateGuides = changeValidateGuidesRequired(customCheckinId, validGuides)
      const errorMessage = document.getElementById(customCheckinId + '-error')
      if (errorMessage) {
        errorMessage.style.visibility = errorMessage.style.visibility === 'visible' ? 'hidden' : ''
      }
      setValidGuides(changeValidateGuides)
    }
    const value = e.target.value
    const name = e.target.name
    const newSelectedInputValue = [...selectedInputValue, { customCheckinId: customCheckinId, [name]: value }]
    setSelectedInputValue(filterUniqueValueById(newSelectedInputValue))
  }

  const onChangeAdditionalGuestInfo = (name, value) => {
    if (listValidateProps.current.includes(name)) {
      if (typeof value === 'string' && !value.trim()) {
        setIsFormValidRequiredFields(false)
      }
    }
    setAdditionalGuestInfo(accomInfo => ({ ...accomInfo, [name]: value }))
  }

  const onShutter = (image: any, nationality: string | undefined, objectKey: string) => {
    if (activateCameraType === 'front') {
      setIdentificationFrontImage(image)
      setIdentificationFrontObjectKey(objectKey)
    } else if (activateCameraType === 'back') {
      setIdentificationBackImage(image)
      setIdentificationBackObjectKey(objectKey)
    } else {
      setRepresentativePassportImage(image)
      setNationality(nationality)
      setRepresentativePassportObjectKey(objectKey)
    }
    setActivateCameraType(undefined)
  }

  const fetchTimeSelection = async () => {
    if (state.selectReservation?.hasSmartCheckin || !checkActivePlugin(state.setting?.plugin, PluginCode.CheckinTimeSelection)) {
      return
    }

    if (!state.selectReservation || state.selectReservation?.selectedReservationAndRooms.length === 0) {
      return
    }

    setIsLoading(true)
    await CancelSpaceReservationFromSelfCI({
      hotelId: state.hotelId,
      reservationIdWithAssignIds: state.selectReservation.selectedReservationAndRooms,
    })
    const selectedReservationIdsWithRoom = state.selectReservation.selectedReservationAndRooms
      .filter(r => r.guestRoomAssignIds.length > 0)
      .map(r => r.reservationId)
    if (selectedReservationIdsWithRoom.length) {
      const res = await fetchTimeSelectionSpace(state.hotelId, selectedReservationIdsWithRoom)
      setTimeSelections(res || [])
    } else {
      setTimeSelections([])
    }
    setIsLoading(false)
  }

  const isValidInputReceiptName = useMemo(() => {
    if (!state.confirm?.payment?.usage.totalAmount) {
      return true
    }
    return inputReceiptName.length > 0
  }, [inputReceiptName])

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'accommodationInfo')) {
      return
    }
    setIsInitializeComponent(true)
    if (state.checkinCustomize) {
      setGuides(state.checkinCustomize.guides)
    }
    // 在庫確認のために常に取得する
    fetchTimeSelection()

    if (state.setting.accommodation.requiredAccompanyNameInput) {
      listValidateProps.current.push('accompany')
    }

    setTimeout(() => {
      const anchor = location.state?.anchor
      if (!anchor) {
        return
      }
      const targetDom = document.getElementById(anchor)
      if (!targetDom) {
        return
      }
      window.scrollTo({
        top: targetDom.getBoundingClientRect().top,
        behavior: 'smooth',
      })
    }, 500)
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }
  return (
    <SelfCheckinLayout
      state={state}
      fullscreen={Boolean(activateCameraType)}
      header={!activateCameraType ? { goToPreviousPage: history.goBack, title: t('Please confirm the contents') } : undefined}
      footer={!activateCameraType ? { right: { isDisabled: isDisabled(), label: t('Save'), onClick: onClickConfirm } } : undefined}
    >
      {!activateCameraType && (
        <>
          <SelfCheckinContentCard id="accommodation-block">
            <AdditionalGuestForm
              additionalGuestInfo={additionalGuestInfo}
              onChange={onChangeAdditionalGuestInfo}
              requiredAccompanyNameInput={state.setting.accommodation.requiredAccompanyNameInput}
              isFormValidRequiredFields={isFormValidRequiredFields}
              imageInfo={{
                documentType: state.uploadLicense?.documentType,
                identificationBackImage,
                identificationFrontImage,
                representativePassportImage,
                nationality,
                setNationality,
                uploadImage: setActivateCameraType,
              }}
              isFirstNameValid={isFirstNameValid}
              isLastNameValid={isLastNameValid}
            />
          </SelfCheckinContentCard>
          {!state.selectReservation?.hasSmartCheckin && Boolean(timeSelections.length) && (
            <SelfCheckinContentCard id="guidance-block">
              <div css={descriptionStyle}>
                <p>{t('Guidance')}</p>
              </div>
              {guides.map(
                (guide, index) =>
                  guide.inputType !== 'none' && (
                    <CustomCheckin
                      key={index}
                      guide={guide}
                      selectedInputValue={selectedInputValue.find(v => v.customCheckinId === guide.customCheckinId)}
                      onChangeState={onChangeState}
                      isEdit={true}
                    />
                  ),
              )}
              <TimeSelectionForm
                timeSelections={timeSelections}
                setIsRequiredTimeSelection={setIsRequiredTimeSelection}
                setSelectedTimes={setSelectedTimes}
                setAssignSpaces={setAssignSpaces}
                isEdit
              />
            </SelfCheckinContentCard>
          )}
          {!!state.confirm?.payment?.usage.totalAmount && (
            <SelfCheckinContentCard id="payment-detail-block">
              <div css={descriptionStyle} style={{ paddingBottom: 32 }}>
                <p>{t('Payment details')}</p>
              </div>
              <div className="reservationLabel">
                <FrontTabletFormLabel label={t('Name of recipient')} required={true} />
                <div css={css({ marginTop: 16 })}>
                  <FrontTabletInputField
                    name="receiptName"
                    value={inputReceiptName}
                    onChange={e => {
                      setInputReceiptName(e.target.value)
                    }}
                    error={!isValidInputReceiptName}
                    placeholder={t('Name of recipient')}
                  />
                </div>
                {!isValidInputReceiptName && <ErrorMessage>{t('Required field has not been entered')}</ErrorMessage>}
              </div>
            </SelfCheckinContentCard>
          )}
        </>
      )}
      {activateCameraType && (
        <IdentityVerifyCamera
          onCancel={() => setActivateCameraType(undefined)}
          onShutter={onShutter}
          type={activateCameraType !== 'representativePassport' ? 'license' : 'passport'}
          side={activateCameraType !== 'representativePassport' ? activateCameraType : undefined}
        />
      )}
    </SelfCheckinLayout>
  )
}

const descriptionStyle = css(
  {
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    padding: '0 0 10px',
    p: {
      fontSize: FrontTabletResponsiveToken.editSectionTitleFontSize.landscape11,
      lineHeight: '27px',
      letterSpacing: '1.8px',
      textAlign: 'center',
      fontWeight: 'bold',
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { p: { fontSize: FrontTabletResponsiveToken.editSectionTitleFontSize.landscape13, lineHeight: '36px' } },
  }),
)
